import { useFormik } from "formik";
import * as yup from "yup";
import axios, { AxiosError, AxiosProgressEvent } from "axios";
import { useUploadProgress } from "@/hooks/useUploadProgress";
import { getIdToken } from "../../utils/firebaseAuth";
import { useRouter } from "next/router";
import { toast } from "react-hot-toast";
import { useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import { FiUploadCloud } from "react-icons/fi";
import Check from "../../svgs/Check";
import Link from "next/link";
import { Checkbox } from "@jammable/ui-core";
import { Input, TextArea } from "@jammable/ui-components/Forms";
import { Spinner } from "@jammable/ui-components/Loader";
import UploadImage from "../UploadImage";
import { z } from "zod";
import CategoryPicker from "../CategoryPicker";

type UploadImageRef = {
  getCroppedImageFile: () => Promise<File>;
};

async function getPresignedUrls(modelName: string) {
  const token = await getIdToken();

  if (!token) {
    throw new Error("Unauthorized");
  }

  const urlResponse = await axios.get(`/api/s3/adminModelPresignedUrl`, {
    params: { modelName },
    headers: { Authorization: token },
  });

  return z
    .object({
      urls: z.array(
        z.object({
          name: z.enum(["pth", "index"]),
          url: z.string(),
          to: z.enum(["s3", "api"]),
        }),
      ),
      urlParam: z.string(),
    })
    .parse(urlResponse.data);
}

type ModelParams = {
  imageUrl: string;
  modelName: string;
  modelDescription: string;
  urlParam: string;
  isPublic: boolean;
  newCategories: string[];
};

async function createModel(params: ModelParams) {
  const token = await getIdToken();

  if (!token) {
    throw new Error("Unauthorized");
  }

  return await axios.post(
    `/api/model/createModel`,
    {
      name: params.modelName,
      description: params.modelDescription,
      image: params.imageUrl,
      urlParam: params.urlParam,
      isPublic: params.isPublic,
      categories: params.newCategories,
    },
    { headers: { Authorization: token } },
  );
}

async function uploadFile(
  file: File,
  onUploadProgress: (event: AxiosProgressEvent) => void,
): Promise<string> {
  const token = await getIdToken();

  const response = await axios.get("/api/s3/getPresignedUrl", {
    params: { type: "voice" },
    headers: { Authorization: token },
  });

  const { url, absoluteUrl } = z
    .object({
      url: z.string(),
      absoluteUrl: z.string(),
    })
    .parse(response.data);

  await axios.put(url, file, {
    headers: { "Content-Type": file.type },
    onUploadProgress,
  });

  return absoluteUrl;
}

type FormValues = {
  modelName: string;
  modelDescription: string;
};

async function validateModelName(name: string) {
  const response = await axios.post(`/api/model/checkDisplayName`, {
    displayName: name,
  });

  return z
    .union([
      z.object({ valid: z.literal(false), message: z.string() }),
      z.object({ valid: z.literal(true) }),
    ])
    .parse(response.data);
}

const UploadVoiceModal = () => {
  const validationSchema = yup.object().shape({
    modelName: yup
      .string()
      .required("Model name is required")
      .max(40, "40 characters maximum."),

    modelDescription: yup.string().max(200, "200 characters maximum."),
  });

  const router = useRouter();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [indexFile, setIndexFile] = useState<File>();
  const [pthFile, setPthFile] = useState<File>();
  const [image, setImage] = useState<File>();
  const [isPublic, setIsPublic] = useState(true);
  const [overallProgress, createUploadProgress] = useUploadProgress();
  const [newCategories, setNewCategories] = useState<string[]>([]);
  const imageUploadRef = useRef<UploadImageRef>(null);

  const handleSubmit = async (values: FormValues) => {
    const file = await imageUploadRef.current?.getCroppedImageFile();

    if (!file) {
      setError("Failed to get cropped image file.");
      setLoading(false);
      return false;
    }

    if (!pthFile) {
      setError("Please select a .pth file!");
      setLoading(false);
      return false;
    }

    if (!agreeTerms) {
      setError("Please agree to our Terms of Use!");
      setLoading(false);

      return false;
    }

    setError(undefined);
    setLoading(true);

    const { modelName, modelDescription } = values;

    // const displayNameValid = await validateModelName(modelName);

    // if (!displayNameValid.valid) {
    //   setError(displayNameValid.message);
    //   setLoading(false);
    //   return;
    // }

    const { urls, urlParam } = await getPresignedUrls(modelName);

    const promises: Promise<unknown>[] = [];

    promises.push(uploadFile(file, createUploadProgress("image")));

    for (const url of urls.filter((x) => x.name === "pth")) {
      promises.push(
        axios.put(url.url, pthFile, {
          headers: { "Content-Type": "application/octet-stream" },
          onUploadProgress: createUploadProgress(`pth-${url.to}`),
        }),
      );
    }

    if (indexFile) {
      for (const url of urls.filter((x) => x.name === "index")) {
        promises.push(
          axios.put(url.url, indexFile, {
            headers: { "Content-Type": "application/octet-stream" },
            onUploadProgress: createUploadProgress(`index-${url.to}`),
          }),
        );
      }
    }

    const [absoluteUrl] = (await Promise.all(promises)) as string[];

    const modelDto: ModelParams = {
      imageUrl: absoluteUrl,
      modelDescription,
      modelName,
      urlParam,
      isPublic,
      newCategories,
    };

    const res = await createModel(modelDto);
    router.push(`/${res.data.data.urlParam}`);
    toast.success("Voice created!");
    setLoading(false);
  };

  const formik = useFormik<FormValues>({
    initialValues: { modelName: "", modelDescription: "" },
    validationSchema,
    onSubmit: async (values) => {
      try {
        await handleSubmit(values);
      } catch (error) {
        setLoading(false);
        if (error instanceof AxiosError) {
          toast.error(error.response?.data?.message);
        } else {
          toast.error("Failed to create voice. Please try again!");
        }
      }
    },
  });

  const { getRootProps: getPthRootProps, getInputProps: getPthInputProps } =
    useDropzone({
      accept: { "application/octet-stream": [".pth"] },
      maxFiles: 1,
      onDrop: (acceptedFiles) => {
        setPthFile(acceptedFiles[0]);
      },
      onDropRejected: () => {
        toast.error("Invalid file type. Please upload a .pth file.");
      },
    });

  const { getRootProps: getIndexRootProps, getInputProps: getIndexInputProps } =
    useDropzone({
      accept: { "application/octet-stream": [".index"] },
      maxFiles: 1,
      onDrop: (acceptedFiles) => {
        const file = acceptedFiles[0];
        if (!file) return;
        setIndexFile(file);
      },
      onDropRejected: () => {
        toast.error("Invalid file type. Please upload a .index file.");
      },
    });

  useDropzone({
    accept: { "image/*": [".jpg", ".png", ".jpeg", ".webp"] },
    maxFiles: 1,
    onDrop: (acceptedFiles) => {
      const file = acceptedFiles[0];
      if (!file) return;
      setImage(file);
    },
    onDropRejected: () => {
      toast.error("Invalid file type. Please upload an image file.");
    },
  });

  return (
    <div className="tw-flex tw-h-max tw-w-full tw-min-w-0 tw-flex-col tw-gap-4 tw-text-jam-ink">
      <div className="tw-pr-10">
        <h2 className="jam-display tw-m-0 tw-text-[26px] tw-font-extrabold">
          Upload a voice
        </h2>
        <div className="tw-mt-1 tw-text-sm tw-font-semibold tw-text-jam-muted">
          Upload an already-trained RVC voice model to Jammable.
        </div>
      </div>

      <form
        onChange={() => setError(undefined)}
        onSubmit={formik.handleSubmit}
        className="tw-flex tw-w-full tw-flex-col tw-gap-4"
      >
        {error && (
          <div className="tw-rounded-[18px] tw-bg-[#FDE4EC] tw-px-4 tw-py-3 tw-text-sm tw-font-bold tw-text-[#8A1C3B]">
            {error}
          </div>
        )}
        <div className="tw-flex tw-w-full tw-flex-col tw-items-center tw-gap-4 sm:tw-flex-row sm:tw-items-start">
          <div className="tw-w-max tw-shrink-0">
            {/* @ts-ignore */}
            <UploadImage setImage={setImage} ref={imageUploadRef} />
          </div>
          <div className="tw-flex tw-w-full tw-min-w-0 tw-flex-col tw-gap-3">
            <Input
              placeholder="Voice name"
              aria-label="Voice name"
              className="tw-h-[52px] tw-rounded-full tw-border-0 tw-bg-jam-lav tw-px-5 tw-text-[15px] tw-text-jam-ink"
              name="modelName"
              value={formik.values.modelName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {formik.touched.modelName && formik.errors.modelName && (
              <div className="tw-text-xs tw-font-bold tw-text-[#8A1C3B]">
                {formik.errors.modelName}
              </div>
            )}
            <TextArea
              value={formik.values.modelDescription}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              name="modelDescription"
              aria-label="Describe your model!"
              className="tw-h-auto tw-min-h-24 tw-w-full tw-rounded-[22px] tw-border-0 tw-bg-jam-lav tw-px-5 tw-text-jam-ink"
              placeholder="(Optional) Describe your voice..."
            />
            {formik.touched.modelDescription &&
              formik.errors.modelDescription && (
                <div className="tw-text-xs tw-font-bold tw-text-[#8A1C3B]">
                  {formik.errors.modelDescription}
                </div>
              )}
          </div>
        </div>
        <div className="tw-flex tw-w-full tw-flex-col tw-gap-2 sm:tw-flex-row">
          {[
            {
              file: pthFile,
              label: ".pth file",
              empty: "Drag and drop a .pth file here",
              root: getPthRootProps,
              input: getPthInputProps,
            },
            {
              file: indexFile,
              label: ".index file (optional)",
              empty: "Drag and drop a .index file here",
              root: getIndexRootProps,
              input: getIndexInputProps,
            },
          ].map((drop) => (
            <div
              key={drop.label}
              className={`tw-flex tw-w-full tw-min-w-0 tw-cursor-pointer tw-flex-col tw-items-center tw-justify-center tw-gap-1.5 tw-rounded-[24px] tw-border-2 tw-border-dashed tw-p-4 tw-text-center ${
                drop.file
                  ? "tw-border-jam-purple tw-bg-jam-lav"
                  : "tw-border-jam-line tw-bg-white hover:tw-border-jam-purple"
              }`}
              {...drop.root()}
            >
              <input {...drop.input()} />
              <span className="tw-flex tw-h-9 tw-w-9 tw-items-center tw-justify-center tw-rounded-full tw-bg-jam-lav tw-text-jam-purple">
                {drop.file ? (
                  <Check size={16} color="#782DC8" />
                ) : (
                  <FiUploadCloud size={16} />
                )}
              </span>
              <div className="tw-text-sm tw-font-extrabold">{drop.label}</div>
              <div className="tw-w-full tw-truncate tw-text-xs tw-font-semibold tw-text-jam-muted">
                {drop.file ? drop.file.name : drop.empty}
              </div>
            </div>
          ))}
        </div>
        <CategoryPicker
          newCategories={newCategories}
          setNewCategories={setNewCategories}
        />
        <div className="tw-flex tw-flex-col tw-gap-2">
          <div className="tw-text-sm tw-font-extrabold">Who can use it</div>
          <div className="tw-flex tw-gap-1 tw-self-stretch tw-rounded-full tw-bg-jam-lav tw-p-1 tw-text-[13px] tw-font-extrabold sm:tw-self-start">
            {(
              [
                [false, "Private, only me"],
                [true, "Public in the library"],
              ] as const
            ).map(([value, label]) => (
              <button
                key={label}
                type="button"
                aria-pressed={isPublic === value}
                onClick={() => setIsPublic(value)}
                className={`tw-flex-grow tw-cursor-pointer tw-rounded-full tw-border-0 tw-px-4 tw-py-2 ${
                  isPublic === value
                    ? "tw-bg-white tw-text-jam-purple"
                    : "tw-bg-transparent tw-text-jam-ink"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="tw-flex tw-items-center tw-gap-3 tw-rounded-[18px] tw-bg-jam-lav tw-px-4 tw-py-3">
          <Checkbox
            checked={agreeTerms}
            onCheckedChange={() => setAgreeTerms(!agreeTerms)}
          />
          <div
            onClick={() => {
              setAgreeTerms(!agreeTerms);
            }}
            className="tw-cursor-pointer tw-text-sm tw-font-bold"
          >
            I agree to Jammable&apos;s{" "}
            <Link
              className="tw-font-extrabold tw-text-jam-purple tw-underline"
              href={`/terms`}
            >
              Terms of Use
            </Link>
          </div>
        </div>

        <button
          disabled={loading}
          type="submit"
          className="tw-flex tw-h-[54px] tw-cursor-pointer tw-items-center tw-justify-center tw-gap-3 tw-rounded-full tw-border-0 tw-bg-jam-lime tw-text-base tw-font-extrabold tw-text-jam-ink disabled:tw-cursor-not-allowed disabled:tw-opacity-70"
        >
          {loading ? (
            <>
              {Math.round(overallProgress)}%
              <Spinner size={4} className="tw-border-3 tw-border-jam-ink" />
            </>
          ) : (
            "Publish"
          )}
        </button>
      </form>
    </div>
  );
};

export default UploadVoiceModal;
